import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTheme } from "../../lib/ThemeContext.jsx";
import {row, card, subText, inputStyle} from "../../lib/styles.js";
import {Card, Label, Btn, DateInp, EmptyState, AnimItem, SaveBadge, ConfirmSheet, StyledSelect} from "../../components/ui.jsx";
import {FONT, RADIUS} from "../../lib/theme.js";
import {uid, currSym} from "../../lib/calculations.js";
import { useMomentumScroll } from "../../hooks/useMomentumScroll.js";

// ─── Fulfilment & Storage Section ─────────────────────────────────────────────
export function FulfilmentSection({ fbmSettings, setFbmSettings, deliveryCreditLimit, setDeliveryCreditLimit, marketplaces = [], saveStatus = "saved", inventoryPools = [], can }) {
  const ro = can && !can("marketplaces","edit"); // read-only for team members
  const T = useTheme();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expandedId, setExpandedId] = useState(null); // collapsed by default
  const [lastEditedId, setLastEditedId] = useState(null);

  function updateFbm(id, patch) {
    if (ro) return;
    setLastEditedId(id);
    setFbmSettings(fbmSettings.map(f => f.id === id ? { ...f, ...patch } : f));
  }
  function addCost() {
    if (ro) return;
    const newId = uid();
    setFbmSettings([{
      id: newId, name: "New Fulfilment Cost", costType: "delivery",
      costPerUnit: 0, flatMonthly: 0,
      triggerType: "credit_limit", creditLimit: 0, creditPeriodDays: 30,
      fixedDay: 1, marketplaceScope: "all", marketplaceIds: [],
      activeMonths: [1,2,3,4,5,6,7,8,9,10,11,12], // all months by default
    }, ...fbmSettings]);
    setExpandedId(newId); // auto-expand new cost
    setLastEditedId(newId);
  }

  return (
    <div>
      <ConfirmSheet open={!!confirmDelete} icon="🗑" title={confirmDelete?`Delete "${confirmDelete.name}"?`:""} message="This fulfilment cost entry will be permanently removed from your cashflow." confirmLabel="Yes, Delete" onConfirm={()=>{ setFbmSettings(fbmSettings.filter(f=>f.id!==confirmDelete.id)); setConfirmDelete(null); }} onCancel={()=>setConfirmDelete(null)}/>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: T.text, letterSpacing: -0.3 }}>Fulfilment & Storage Costs</div>
          <div style={{ fontSize: 14, color: T.textSub, marginTop: 4 }}>Costs from your warehouse, 3PL, or fulfillment center. These appear in Monthly View as expenses.</div>
        </div>
        <Btn onClick={addCost} color={T.green}>+ Add Cost</Btn>
      </div>

      <div style={{ background: T.accent + "10", border: `1px solid ${T.accent + "33"}`, borderRadius: RADIUS.md, padding: "14px 18px", marginBottom: 20, fontSize: 13, color: T.textSub, lineHeight: 1.7 }}>
        <strong style={{ color: T.accent }}>Cost per unit shipped:</strong> Leave blank (or set to 0) if you already entered a Delivery/unit value on your product in Marketplaces — that value takes priority. Only enter a value here if your products have Delivery/unit set to 0.
      </div>

      {fbmSettings.length === 0 && <EmptyState icon="🏭" title="No fulfilment costs yet" description="Add your warehouse or fulfilment centre costs here." action={<Btn onClick={addCost} color={T.green}>+ Add Cost</Btn>}/>}

      {fbmSettings.map(fbm => {
        const isExpanded = expandedId === fbm.id;
        const costLabel = fbm.costType === "storage" ? "🏗 Storage" : fbm.costType === "flat" ? "📋 Flat" : "📦 Delivery";
        const trigger = fbm.triggerType === "fixed_day" ? "Fixed day" : fbm.triggerType === "credit_period" ? `${fbm.creditPeriodDays}d credit` : fbm.creditLimit > 0 ? `€${fbm.creditLimit.toLocaleString()} limit` : "No trigger";
        return (
        <AnimItem key={fbm.id}>
          <Card style={{ marginBottom: 10, padding: 0, overflow: "hidden" }}>
            {/* Collapsible header */}
            <div style={{ ...row(12), padding:"14px 18px", cursor:"pointer" }} onClick={() => setExpandedId(isExpanded ? null : fbm.id)}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ ...row(8) }}>
                  <span style={{ fontSize:15, fontWeight:700, color:T.text }}>{fbm.name}</span>
                  <SaveBadge status={saveStatus} show={lastEditedId === fbm.id}/>
                </div>
                <div style={{ ...subText(T), marginTop:2 }}>{costLabel} · {trigger}{fbm.marketplaceScope !== "all" ? ` · ${(fbm.marketplaceIds||[]).length} marketplace(s)` : ""}</div>
              </div>
              <Btn onClick={e => { e.stopPropagation(); setConfirmDelete(fbm); }} variant="ghost" size="sm" color={T.red} danger>Delete</Btn>
              <span style={{ color:T.textSub, fontSize:13 }}>{isExpanded ? "▲" : "▼"}</span>
            </div>

            {isExpanded && (
            <div style={{ padding:"0 18px 18px", borderTop:`1px solid ${T.border}` }}>
              <div style={{ paddingTop:14 }}>
              <div style={{ marginBottom:14 }}>
                <Label style={{ marginBottom:6 }}>Name</Label>
                <input value={fbm.name} onChange={e => updateFbm(fbm.id, { name: e.target.value })} className="sf-editable"
                  style={{ background:"transparent",fontSize:15,fontWeight:600,color:T.text,fontFamily:FONT,width:"100%" }}/>
              </div>

            {/* Cost Type */}
            <Label style={{ marginBottom: 8 }}>What type of cost?</Label>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { v: "delivery", l: "📦 Delivery / Outbound", tip: "Cost per unit shipped to customers. Accrues daily based on unit targets." },
                { v: "storage",  l: "🏗 Storage",             tip: "Monthly cost for storing inventory. Billed once per month." },
                { v: "flat",     l: "📋 Flat Monthly Fee",    tip: "Fixed amount billed each month regardless of units." },
              ].map(t => (
                <button key={t.v} onClick={() => updateFbm(fbm.id, { costType: t.v })} style={{
                  background: fbm.costType === t.v ? T.accent+"18" : T.surface,
                  color: fbm.costType === t.v ? T.accent : T.textSub,
                  border: `1px solid ${fbm.costType === t.v ? T.accent : T.border}`,
                  borderRadius: RADIUS.md, padding: "10px 16px", fontSize: 13,
                  fontFamily: FONT, cursor: "pointer", fontWeight: fbm.costType === t.v ? 600 : 400, transition: "all 0.15s",
                }}>{t.l}</button>
              ))}
            </div>

            {/* Which marketplaces — including shared-pool shortcuts.
                Shared pools (isShared=true) appear as one-click buttons that
                set marketplaceIds to all the pool's members. This matches the
                pool semantics: a shared pool is one physical inventory bucket
                so a fulfillment cost that applies to "the pool" naturally
                covers every channel selling from it. */}
            {marketplaces.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <Label style={{ marginBottom: 8 }} tip="fulfilmentScope">Applies to</Label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => updateFbm(fbm.id, { marketplaceScope: "all", marketplaceIds: [] })} style={{
                    background: fbm.marketplaceScope === "all" ? T.accent+"18" : "transparent",
                    color: fbm.marketplaceScope === "all" ? T.accent : T.textSub,
                    border: `1px solid ${fbm.marketplaceScope === "all" ? T.accent : T.border}`,
                    borderRadius: RADIUS.sm, padding: "5px 14px", fontSize: 13, fontFamily: FONT, cursor: "pointer",
                  }}>All Marketplaces</button>
                  {(inventoryPools || []).filter(p => p.isShared && (p.marketplaceIds||[]).length > 0).map(p => {
                    const ids = p.marketplaceIds || [];
                    const sel = fbm.marketplaceScope !== "all"
                      && (fbm.marketplaceIds || []).length === ids.length
                      && ids.every(id => (fbm.marketplaceIds || []).includes(id));
                    return (
                      <button key={p.id}
                        onClick={() => updateFbm(fbm.id, { marketplaceScope: "specific", marketplaceIds: [...ids] })}
                        title={`Shared pool — applies the cost to all ${ids.length} member marketplaces`}
                        style={{
                          background: sel ? T.accent+"22" : "transparent",
                          color: sel ? T.accent : T.textSub,
                          border: `1px solid ${sel ? T.accent : T.border}`,
                          borderRadius: RADIUS.sm, padding: "5px 14px", fontSize: 13, fontFamily: FONT, cursor: "pointer",
                          fontWeight: sel ? 700 : 400,
                        }}>
                        🔗 {p.name}
                      </button>
                    );
                  })}
                  {marketplaces.map(mkt => {
                    const sel = fbm.marketplaceScope !== "all" && (fbm.marketplaceIds || []).includes(mkt.id);
                    return (
                      <button key={mkt.id} onClick={() => {
                        const cur = fbm.marketplaceIds || [];
                        const next = sel ? cur.filter(x => x !== mkt.id) : [...cur, mkt.id];
                        updateFbm(fbm.id, { marketplaceScope: "specific", marketplaceIds: next });
                      }} style={{
                        background: sel ? T.purple+"18" : "transparent",
                        color: sel ? T.purple : T.textSub,
                        border: `1px solid ${sel ? T.purple : T.border}`,
                        borderRadius: RADIUS.sm, padding: "5px 14px", fontSize: 13, fontFamily: FONT, cursor: "pointer",
                      }}>{mkt.name}</button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Active months — seasonal pricing support. Click a month
                to toggle it. Create two entries (one for Jan-Sep at $3.50,
                one for Oct-Dec at $4.25) for peak/off-peak rates. */}
            <div style={{ marginBottom: 16 }}>
              <Label style={{ marginBottom: 6 }}>Active months</Label>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((lbl, i) => {
                  const m = i + 1;
                  const active = (fbm.activeMonths || [1,2,3,4,5,6,7,8,9,10,11,12]).includes(m);
                  return (
                    <button key={m} onClick={() => {
                      const cur = fbm.activeMonths || [1,2,3,4,5,6,7,8,9,10,11,12];
                      const next = active ? cur.filter(x => x !== m) : [...cur, m].sort((a,b)=>a-b);
                      updateFbm(fbm.id, { activeMonths: next.length ? next : [m] }); // prevent empty
                    }} style={{
                      padding:"3px 8px", borderRadius:RADIUS.sm, fontSize:11, fontFamily:FONT,
                      cursor:"pointer", fontWeight:active?700:400,
                      background:active?T.accent+"18":"transparent",
                      border:`1px solid ${active?T.accent:T.border}`,
                      color:active?T.accent:T.textSub,
                    }}>{lbl}</button>
                  );
                })}
              </div>
              {(fbm.activeMonths||[]).length < 12 && (
                <div style={{ fontSize:10, color:T.orange, marginTop:4 }}>
                  Active {(fbm.activeMonths||[]).length} of 12 months — this cost only applies in selected months
                </div>
              )}
            </div>

            {/* Cost amount */}
            {fbm.costType === "delivery" && (
              <div style={{ marginBottom: 16 }}>
                <Label style={{ marginBottom: 6 }} tip="fulfilmentCostPerUnit">Cost per unit shipped (€)</Label>
                <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 8 }}>
                  <span style={{ color: T.textSub }}>{currSym()}</span>
                  <input type="number" value={fbm.costPerUnit || ""} placeholder="0" onChange={e => updateFbm(fbm.id, { costPerUnit: parseFloat(e.target.value) || 0 })}
                    style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.md, color: T.text, padding: "9px 12px", fontSize: 15, width: 160, outline: "none", fontFamily: FONT }}/>
                </div>
                {/* Override checkbox */}
                <label style={{ ...row(8), cursor:"pointer", userSelect:"none", marginBottom:10 }}>
                  <input type="checkbox" checked={!!fbm.overrideProductDelivery} onChange={e => updateFbm(fbm.id, { overrideProductDelivery: e.target.checked })}
                    style={{ width:16, height:16, accentColor: T.orange, cursor:"pointer" }}/>
                  <span style={{ fontSize:13, color: fbm.overrideProductDelivery ? T.orange : T.textSub, fontWeight: fbm.overrideProductDelivery ? 600 : 400 }}>
                    Override product-level Delivery/unit — use this value for ALL products
                  </span>
                </label>

                {/* Applied-to feedback */}
                {fbm.costPerUnit > 0 && marketplaces.length > 0 && (() => {
                  const applied = []; // {mktName, prodName}
                  const skipped = [];
                  for (const mkt of marketplaces) {
                    for (const prod of mkt.products) {
                      if (fbm.overrideProductDelivery || !prod.deliveryCost) {
                        applied.push({ mkt: mkt.name, prod: prod.name });
                      } else {
                        skipped.push({ mkt: mkt.name, prod: prod.name, own: prod.deliveryCost });
                      }
                    }
                  }
                  return (
                    <div style={{ background: T.surface, borderRadius: RADIUS.md, padding:"12px 14px", border:`1px solid ${T.border}`, fontSize:12 }}>
                      <div style={{ fontWeight:700, color:T.text, marginBottom:8 }}>
                        This cost applies to <span style={{ color:T.green }}>{applied.length}</span> product{applied.length!==1?"s":""}{skipped.length>0?`, skips ${skipped.length}`:""}
                      </div>
                      {applied.length > 0 && (
                        <div style={{ marginBottom:8 }}>
                          <div style={{ color:T.green, fontWeight:600, marginBottom:4 }}>✓ Applied to:</div>
                          {applied.map((a,i) => (
                            <div key={i} style={{ color:T.textSub, paddingLeft:10, marginBottom:2 }}>
                              {a.mkt} → {a.prod}
                            </div>
                          ))}
                        </div>
                      )}
                      {skipped.length > 0 && (
                        <div>
                          <div style={{ color:T.orange, fontWeight:600, marginBottom:4 }}>⟳ Skipped (uses own value):</div>
                          {skipped.map((s,i) => (
                            <div key={i} style={{ color:T.textSub, paddingLeft:10, marginBottom:2 }}>
                              {s.mkt} → {s.prod} <span style={{ color:T.orange }}>€{s.own}/unit</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {applied.length === 0 && skipped.length === 0 && (
                        <div style={{ color:T.textSub }}>No products configured yet — add products in Marketplaces tab.</div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            {fbm.costType === "storage" && (
              <div style={{ marginBottom: 16 }}>
                <Label style={{ marginBottom: 8 }}>How is storage calculated?</Label>
                <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  {[
                    { v: "per_unit",    l: "Per unit / month" },
                    { v: "pallet_mix",  l: "Standard + Oversize pallets" },
                    { v: "std_pallet",  l: "Standard pallets only" },
                    { v: "os_pallet",   l: "Oversize pallets only" },
                    { v: "flat",        l: "Flat monthly" },
                  ].map(t => (
                    <button key={t.v} onClick={() => updateFbm(fbm.id, { storageCalc: t.v })} style={{
                      background: fbm.storageCalc === t.v ? T.accent+"18" : "transparent",
                      color: fbm.storageCalc === t.v ? T.accent : T.textSub,
                      border: `1px solid ${fbm.storageCalc === t.v ? T.accent : T.border}`,
                      borderRadius: RADIUS.sm, padding: "5px 12px", fontSize: 12, fontFamily: FONT, cursor: "pointer",
                      fontWeight: fbm.storageCalc === t.v ? 600 : 400,
                    }}>{t.l}</button>
                  ))}
                </div>

                {(fbm.storageCalc === "per_unit" || !fbm.storageCalc) && (
                  <div style={{ display:"flex",gap:5,alignItems:"center" }}>
                    <span style={{ color:T.textSub }}>{currSym()}</span>
                    <input type="number" value={fbm.costPerUnit||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{costPerUnit:parseFloat(e.target.value)||0})} style={{ ...inputStyle(T), width:120 }}/>
                    <span style={{ ...subText(T) }}>per unit / month</span>
                  </div>
                )}

                {/* Standard pallets only */}
                {fbm.storageCalc === "std_pallet" && (
                  <div style={{ display:"flex",gap:14,flexWrap:"wrap" }}>
                    <div><Label style={{ marginBottom:4 }}>Standard pallets in storage</Label><input type="number" value={fbm.stdPallets||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{stdPallets:parseInt(e.target.value)||0})} style={{ ...inputStyle(T), width:100 }}/></div>
                    <div><Label style={{ marginBottom:4 }}>Cost per pallet / month</Label><div style={{ display:"flex",gap:5,alignItems:"center" }}><span style={{ color:T.textSub }}>{currSym()}</span><input type="number" value={fbm.stdPalletCost||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{stdPalletCost:parseFloat(e.target.value)||0})} style={{ ...inputStyle(T), width:120 }}/></div></div>
                    <div style={{ ...subText(T),alignSelf:"flex-end",paddingBottom:10 }}>Total: <strong style={{ color:T.text }}>€{((fbm.stdPallets||0)*(fbm.stdPalletCost||0)).toLocaleString()}/mo</strong></div>
                  </div>
                )}

                {/* Oversize pallets only */}
                {fbm.storageCalc === "os_pallet" && (
                  <div style={{ display:"flex",gap:14,flexWrap:"wrap" }}>
                    <div><Label style={{ marginBottom:4 }}>Oversize pallets in storage</Label><input type="number" value={fbm.osPallets||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{osPallets:parseInt(e.target.value)||0})} style={{ ...inputStyle(T), width:100 }}/></div>
                    <div><Label style={{ marginBottom:4 }}>Cost per pallet / month</Label><div style={{ display:"flex",gap:5,alignItems:"center" }}><span style={{ color:T.textSub }}>{currSym()}</span><input type="number" value={fbm.osPalletCost||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{osPalletCost:parseFloat(e.target.value)||0})} style={{ ...inputStyle(T), width:120 }}/></div></div>
                    <div style={{ ...subText(T),alignSelf:"flex-end",paddingBottom:10 }}>Total: <strong style={{ color:T.text }}>€{((fbm.osPallets||0)*(fbm.osPalletCost||0)).toLocaleString()}/mo</strong></div>
                  </div>
                )}

                {/* Mixed pallets — both types simultaneously */}
                {fbm.storageCalc === "pallet_mix" && (
                  <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
                    {/* Standard row */}
                    <div style={{ background:T.surface,borderRadius:RADIUS.md,padding:"12px 14px",border:`1px solid ${T.border}` }}>
                      <div style={{ fontSize:12,fontWeight:700,color:T.accent,marginBottom:8,letterSpacing:0.5 }}>📦 STANDARD PALLETS</div>
                      <div style={{ display:"flex",gap:14,flexWrap:"wrap" }}>
                        <div><Label style={{ marginBottom:4 }}>Qty in storage</Label><input type="number" value={fbm.stdPallets||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{stdPallets:parseInt(e.target.value)||0})} style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,padding:"7px 10px",fontSize:14,width:90,outline:"none",fontFamily:FONT }}/></div>
                        <div><Label style={{ marginBottom:4 }}>pallet / month</Label><div style={{ display:"flex",gap:5,alignItems:"center" }}><span style={{ color:T.textSub }}>{currSym()}</span><input type="number" value={fbm.stdPalletCost||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{stdPalletCost:parseFloat(e.target.value)||0})} style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,padding:"7px 10px",fontSize:14,width:110,outline:"none",fontFamily:FONT }}/></div></div>
                        <div style={{ ...subText(T),alignSelf:"flex-end",paddingBottom:4 }}>= <strong style={{ color:T.accent }}>€{((fbm.stdPallets||0)*(fbm.stdPalletCost||0)).toLocaleString()}/mo</strong></div>
                      </div>
                    </div>
                    {/* Oversize row */}
                    <div style={{ background:T.surface,borderRadius:RADIUS.md,padding:"12px 14px",border:`1px solid ${T.border}` }}>
                      <div style={{ fontSize:12,fontWeight:700,color:T.purple,marginBottom:8,letterSpacing:0.5 }}>🏗 OVERSIZE PALLETS</div>
                      <div style={{ display:"flex",gap:14,flexWrap:"wrap" }}>
                        <div><Label style={{ marginBottom:4 }}>Qty in storage</Label><input type="number" value={fbm.osPallets||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{osPallets:parseInt(e.target.value)||0})} style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,padding:"7px 10px",fontSize:14,width:90,outline:"none",fontFamily:FONT }}/></div>
                        <div><Label style={{ marginBottom:4 }}>pallet / month</Label><div style={{ display:"flex",gap:5,alignItems:"center" }}><span style={{ color:T.textSub }}>{currSym()}</span><input type="number" value={fbm.osPalletCost||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{osPalletCost:parseFloat(e.target.value)||0})} style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,padding:"7px 10px",fontSize:14,width:110,outline:"none",fontFamily:FONT }}/></div></div>
                        <div style={{ ...subText(T),alignSelf:"flex-end",paddingBottom:4 }}>= <strong style={{ color:T.purple }}>€{((fbm.osPallets||0)*(fbm.osPalletCost||0)).toLocaleString()}/mo</strong></div>
                      </div>
                    </div>
                    {/* Combined total */}
                    <div style={{ display:"flex",justifyContent:"flex-end",fontSize:13,color:T.textSub }}>
                      Combined total: <strong style={{ color:T.text,marginLeft:6 }}>€{(((fbm.stdPallets||0)*(fbm.stdPalletCost||0))+((fbm.osPallets||0)*(fbm.osPalletCost||0))).toLocaleString()}/month</strong>
                    </div>
                  </div>
                )}

                {fbm.storageCalc === "flat" && (
                  <div style={{ display:"flex",gap:5,alignItems:"center" }}>
                    <span style={{ color:T.textSub }}>{currSym()}</span>
                    <input type="number" value={fbm.flatMonthly||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{flatMonthly:parseFloat(e.target.value)||0})} style={{ ...inputStyle(T), width:150 }}/>
                    <span style={{ ...subText(T) }}>/ month</span>
                  </div>
                )}
              </div>
            )}
            {fbm.costType === "flat" && (
              <div style={{ marginBottom: 16 }}>
                <Label style={{ marginBottom: 6 }}>Monthly flat fee (€)</Label>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  <span style={{ color: T.textSub }}>{currSym()}</span>
                  <input type="number" value={fbm.flatMonthly||""} placeholder="0" onChange={e=>updateFbm(fbm.id,{flatMonthly:parseFloat(e.target.value)||0})} style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:RADIUS.md,color:T.text,padding:"9px 12px",fontSize:15,width:150,outline:"none",fontFamily:FONT }}/>
                </div>
              </div>
            )}

            {/* Payment trigger */}
            <Label style={{ marginBottom: 8 }} tip="fulfilmentBillingTrigger">When do you get billed?</Label>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                { v: "credit_limit",  l: "💳 Credit Limit",      tip: "You pay when accumulated costs hit a spending limit." },
                { v: "credit_period", l: "📅 Period Credit",      tip: "Billed every X days regardless of amount spent." },
                { v: "both",          l: "⚡ Whichever First",   tip: "Pay when limit is hit OR when period expires — whichever comes first." },
                { v: "fixed_day",     l: "🗓 Fixed Day of Month", tip: "Invoice on a specific day every month." },
              ].map(t => (
                <button key={t.v} onClick={() => updateFbm(fbm.id, { triggerType: t.v })} title={t.tip} style={{
                  background: fbm.triggerType === t.v ? T.orange+"22" : T.surface,
                  color: fbm.triggerType === t.v ? T.orange : T.textSub,
                  border: `1px solid ${fbm.triggerType === t.v ? T.orange : T.border}`,
                  borderRadius: RADIUS.md, padding: "8px 14px", fontSize: 13,
                  fontFamily: FONT, cursor: "pointer", fontWeight: fbm.triggerType === t.v ? 600 : 400, transition: "all 0.15s",
                }}>{t.l}</button>
              ))}
            </div>

            {(fbm.triggerType === "credit_limit" || fbm.triggerType === "both") && (
              <div style={{ marginBottom: 14 }}>
                <Label style={{ marginBottom: 6 }} tip="creditLimit">Credit Limit (€)</Label>
                <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 8 }}>
                  <span style={{ color: T.textSub }}>{currSym()}</span>
                  <input type="number" value={fbm.creditLimit||""} placeholder="e.g. 50000" onChange={e=>updateFbm(fbm.id,{creditLimit:parseFloat(e.target.value)||0})}
                    style={{ ...inputStyle(T), width:150 }}/>
                </div>
                <Label style={{ marginBottom:6 }} tip="fulfilmentLimitCycleStart">Limit cycle starts (optional)</Label>
                <DateInp value={fbm.creditLimitStart||""} onChange={v=>updateFbm(fbm.id,{creditLimitStart:v})} placeholder="Select start date"/>
              </div>
            )}

            {(fbm.triggerType === "credit_period" || fbm.triggerType === "both") && (
              <div style={{ marginBottom: 14 }}>
                <Label style={{ marginBottom: 6 }}>Credit Period (days)</Label>
                <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 6 }}>
                  <input type="number" value={fbm.creditPeriodDays||""} placeholder="30" onChange={e=>updateFbm(fbm.id,{creditPeriodDays:parseInt(e.target.value)||30})}
                    style={{ ...inputStyle(T), width:100 }}/>
                  <span style={{ color:T.textSub,fontSize:13 }}>days</span>
                </div>
                <div style={{ fontSize:12,color:T.accent,background:T.accent+"12",border:`1px solid ${T.accent+"33"}`,borderRadius:RADIUS.sm,padding:"7px 12px",marginBottom:8 }}>
                  💡 You are billed every <strong>{fbm.creditPeriodDays||30} days</strong> regardless of how much you have spent — even if the balance is €0.
                </div>
                <Label style={{ marginBottom:6 }} tip="fulfilmentPeriodStart">Period starts on</Label>
                <DateInp value={fbm.creditPeriodStart||""} onChange={v=>updateFbm(fbm.id,{creditPeriodStart:v})} placeholder="Select start date"/>
              </div>
            )}

            {fbm.triggerType === "fixed_day" && (
              <div style={{ marginBottom: 12 }}>
                <Label style={{ marginBottom: 6 }}>Invoice Day of Month</Label>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <StyledSelect value={fbm.fixedDay||1} onChange={e=>updateFbm(fbm.id,{fixedDay:parseInt(e.target.value)})}>
                    {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1 === 31 ? "31 (last day)" : i+1}</option>)}
                  </StyledSelect>
                  <span style={{ color:T.textSub,fontSize:13 }}>of each month</span>
                  <div style={{ width:"100%", marginTop:6 }}>
                    <Label style={{ marginBottom:6 }} tip="fulfilmentBillingStartFrom">Billing starts from (optional)</Label>
                    <DateInp value={fbm.fixedDayStart||""} onChange={v=>updateFbm(fbm.id,{fixedDayStart:v})} placeholder="Select start date"/>
                  </div>
                </div>
              </div>
            )}
            </div>{/* end paddingTop:14 */}
            </div>/* end isExpanded */
            )}
          </Card>
        </AnimItem>
        );
      })}
    </div>
  );
}

